// Renders an assistant answer's markdown as React elements — headings,
// bold/italic, lists, inline code, fenced code and tables. Everything the
// model wrote lands in the DOM as React text nodes (no innerHTML anywhere),
// so raw HTML in the answer is displayed literally, never executed.
// Colors come from the theme tokens, so both light and dark themes work.
import { Fragment } from 'react';
import { parseMarkdown } from './markdown';

const CODE_INLINE = {
  fontFamily: 'var(--mono)',
  fontSize: '0.88em',
  background: 'var(--surface-1, #ffffff)',
  border: '1px solid var(--border-soft, #eceff6)',
  borderRadius: 6,
  padding: '1px 6px',
};
const CODE_BLOCK = {
  fontFamily: 'var(--mono)',
  fontSize: 12.5,
  lineHeight: 1.6,
  background: 'var(--surface-1, #ffffff)',
  border: '1px solid var(--border-soft, #eceff6)',
  borderRadius: 10,
  padding: '10px 12px',
  overflowX: 'auto',
  whiteSpace: 'pre',
  margin: '10px 0',
};
// The wrapper — not the bubble — scrolls sideways, so a wide table can't
// blow up the layout on a 375px phone screen.
const TABLE_WRAP = {
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  maxWidth: '100%',
  margin: '10px 0',
};
const TABLE = { borderCollapse: 'collapse', fontSize: 13.5, lineHeight: 1.5 };
const TH = {
  textAlign: 'left',
  fontWeight: 700,
  padding: '6px 10px',
  borderBottom: '2px solid var(--border, #e3e7f0)',
  whiteSpace: 'nowrap',
};
const TD = {
  padding: '6px 10px',
  borderBottom: '1px solid var(--border-soft, #eceff6)',
  verticalAlign: 'top',
};
const LIST = { margin: '8px 0', paddingLeft: 22 };
const LIST_ITEM = { margin: '3px 0' };
const PARAGRAPH = { margin: '8px 0' };
const HR = { border: 'none', borderTop: '1px solid var(--border, #e3e7f0)', margin: '12px 0' };

function headingStyle(level) {
  return {
    fontSize: level <= 2 ? 15.5 : level === 3 ? 14.5 : 13.5,
    fontWeight: level <= 2 ? 800 : 700,
    lineHeight: 1.4,
    margin: '14px 0 6px',
  };
}

function Inline({ tokens }) {
  return tokens.map((t, i) => {
    if (t.type === 'code')
      return (
        <code key={i} style={CODE_INLINE}>
          {t.content}
        </code>
      );
    if (t.type === 'bold') return <strong key={i}>{t.content}</strong>;
    if (t.type === 'italic') return <em key={i}>{t.content}</em>;
    return <Fragment key={i}>{t.content}</Fragment>;
  });
}

function Block({ block, style }) {
  switch (block.type) {
    case 'heading': {
      // Levels map to h4/h5 (h1–h3 belong to the page chrome, and the
      // visual size comes from headingStyle anyway).
      const Tag = block.level <= 2 ? 'h4' : 'h5';
      return (
        <Tag style={{ ...headingStyle(block.level), ...style }}>
          <Inline tokens={block.inlines} />
        </Tag>
      );
    }
    case 'ul':
    case 'ol': {
      const Tag = block.type;
      return (
        <Tag
          style={{ ...LIST, ...style }}
          {...(block.type === 'ol' && block.start !== 1 ? { start: block.start } : {})}
        >
          {block.items.map((item, i) => (
            <li key={i} style={LIST_ITEM}>
              <Inline tokens={item} />
            </li>
          ))}
        </Tag>
      );
    }
    case 'table':
      return (
        <div style={{ ...TABLE_WRAP, ...style }} data-md-table-wrap="true">
          <table style={TABLE}>
            <thead>
              <tr>
                {block.header.map((cell, i) => (
                  <th key={i} style={TH}>
                    <Inline tokens={cell} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} style={TD}>
                      <Inline tokens={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'code':
      return (
        <pre style={{ ...CODE_BLOCK, ...style }}>
          <code style={{ fontFamily: 'inherit' }}>{block.content}</code>
        </pre>
      );
    case 'hr':
      return <hr style={{ ...HR, ...style }} />;
    default:
      // paragraph — its lines are separated by <br>, not pre-wrap
      return (
        <p style={{ ...PARAGRAPH, ...style }}>
          {block.lines.map((tokens, i) => (
            <Fragment key={i}>
              {i > 0 && <br />}
              <Inline tokens={tokens} />
            </Fragment>
          ))}
        </p>
      );
  }
}

export default function MarkdownMessage({ text }) {
  const blocks = parseMarkdown(text);
  return (
    <div>
      {blocks.map((block, i) => (
        <Block
          key={i}
          block={block}
          // Trim the outer margins so the bubble padding frames the content.
          style={{
            ...(i === 0 ? { marginTop: 0 } : null),
            ...(i === blocks.length - 1 ? { marginBottom: 0 } : null),
          }}
        />
      ))}
    </div>
  );
}
